import { computed } from "mobx";
import { useMemo } from "react";
import { useFilterSettingsStore, useLeadStore, usePipedriveStore } from "..";
import ILead, { LeadStatus } from "../interfaces/ILead";
import { getMinMax } from "../utils/utils";
import useHasReminder from "./useHasReminder";

export default function (leads: ILead[]): ILead[] {
    const { filters: { leadSettings: leadFilters } } = useFilterSettingsStore();
    const pipedriveStore = usePipedriveStore();
    const hasReminder = useHasReminder();

    const numberFilteredLeads = useMemo(() => {
        return leads.filter(l =>
            (!leadFilters.minLotCoverage || l.lotCoverage && l.lotCoverage >= leadFilters.minLotCoverage) &&
            (!leadFilters.maxLotCoverage || l.lotCoverage && l.lotCoverage <= leadFilters.maxLotCoverage) &&
            (!leadFilters.minBuildingSize || l.buildingSize && l.buildingSize >= leadFilters.minBuildingSize) &&
            (!leadFilters.maxBuildingSize || l.buildingSize && l.buildingSize <= leadFilters.maxBuildingSize) &&
            (!leadFilters.minLandSize || l.landSize && l.landSize >= leadFilters.minLandSize) &&
            (!leadFilters.maxLandSize || l.landSize && l.landSize <= leadFilters.maxLandSize) &&
            (!leadFilters.minUsableArea || l.usableArea && l.usableArea >= leadFilters.minUsableArea) &&
            (!leadFilters.maxUsableArea || l.usableArea && l.usableArea <= leadFilters.maxUsableArea) &&
            (!leadFilters.minDateModified || l.modifiedDate && l.modifiedDate >= leadFilters.minDateModified) &&
            (!leadFilters.maxDateModified || l.modifiedDate && l.modifiedDate <= leadFilters.maxDateModified)
        );
    }, [
        leads,
        leadFilters.minLotCoverage, leadFilters.maxLotCoverage,
        leadFilters.minBuildingSize, leadFilters.maxBuildingSize,
        leadFilters.minLandSize, leadFilters.maxLandSize,
        leadFilters.minUsableArea, leadFilters.maxUsableArea,
        leadFilters.minDateModified, leadFilters.maxDateModified
    ]);

    const statusAndLabelFilteredLeads = useMemo(() => {
        if (!leadFilters.statuses.length && !leadFilters.labelIds.length)
            return numberFilteredLeads;

        return numberFilteredLeads.filter(l =>
            (!leadFilters.statuses.length || leadFilters.statuses.includes(l.status as LeadStatus)) &&
            (!leadFilters.labelIds.length || (l.labelId && leadFilters.labelIds.includes(l.labelId)))
        );
    }, [numberFilteredLeads, leadFilters.statuses, leadFilters.labelIds]);

    const reminderFilteredLeads = useMemo(() => {
        if (leadFilters.showWithReminder && leadFilters.showWithoutReminder)
            return statusAndLabelFilteredLeads;

        return statusAndLabelFilteredLeads.filter(l => hasReminder(l) ? leadFilters.showWithReminder : leadFilters.showWithoutReminder);
    }, [statusAndLabelFilteredLeads, hasReminder, leadFilters.showWithReminder, leadFilters.showWithoutReminder]);

    const locationGradeFilteredLeads = useMemo(() => {
        if (!leadFilters.locationGrades.length)
            return reminderFilteredLeads;

        return reminderFilteredLeads.filter(l => l.locationGrade && leadFilters.locationGrades.includes(l.locationGrade));
    }, [reminderFilteredLeads, leadFilters.locationGrades]);

    const propertyGradeFilteredLeads = useMemo(() => {
        if (!leadFilters.propertyGrades.length)
            return locationGradeFilteredLeads;

        return locationGradeFilteredLeads.filter(l => l.propertyGrade && leadFilters.propertyGrades.includes(l.propertyGrade));
    }, [locationGradeFilteredLeads, leadFilters.propertyGrades]);

    const ownershipTypeFilteredLeads = useMemo(() => {
        if (!leadFilters.ownershipTypes.length)
            return propertyGradeFilteredLeads;

        return propertyGradeFilteredLeads.filter(l => l.ownershipType && leadFilters.ownershipTypes.includes(l.ownershipType));
    }, [propertyGradeFilteredLeads, leadFilters.ownershipTypes]);

    const companyFilteredLeads = useMemo(() => {
        if (!leadFilters.pipedriveCompanyName)
            return ownershipTypeFilteredLeads;

        const pipedriveCompanyIds = pipedriveStore.organizations.filter(o => o.name === leadFilters.pipedriveCompanyName).map(o => o.id);
        return ownershipTypeFilteredLeads.filter(l => l.pipedriveCompanyId && pipedriveCompanyIds.includes(l.pipedriveCompanyId));
    }, [ownershipTypeFilteredLeads, leadFilters.pipedriveCompanyName, pipedriveStore.organizations]);

    return useMemo(() => {
        if (!leadFilters.keyword)
            return companyFilteredLeads;

        const keywordToLowerCase = leadFilters.keyword.toLowerCase();
        return companyFilteredLeads.filter(l =>
            (l.address && l.address.toLowerCase().includes(keywordToLowerCase)) ||
            (l.ownerName && l.ownerName.toLowerCase().includes(keywordToLowerCase)) ||
            (l.ownerAddress && l.ownerAddress.toLowerCase().includes(keywordToLowerCase)) ||
            (l.phoneNumber && l.phoneNumber.toLowerCase().includes(keywordToLowerCase)) ||
            (l.notes.some(n => n.text.toLowerCase().includes(keywordToLowerCase)))
        );
    }, [companyFilteredLeads, leadFilters.keyword]);
}

export const useMinMaxLotCoverage = () => {
    const leadStore = useLeadStore();
    return useMemo(() => computed(() => getMinMax(leadStore.leads, lead => lead.lotCoverage)), []).get();
}

export const useMinMaxBuildingSize = () => {
    const leadStore = useLeadStore();
    return useMemo(() => computed(() => getMinMax(leadStore.leads, lead => lead.buildingSize)), []).get();
}

export const useMinMaxLandSize = () => {
    const leadStore = useLeadStore();
    return useMemo(() => computed(() => getMinMax(leadStore.leads, lead => lead.landSize)), []).get();
}